import React, { useCallback, useEffect, useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
type ScannedProduct = {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
};
const US_CITIES = ['Houston, TX', 'Phoenix, AZ', 'San Antonio, TX', 'Dallas, TX', 'Jacksonville, FL', 'Columbus, OH', 'Charlotte, NC', 'Indianapolis, IN', 'San Diego, CA', 'Fort Worth, TX', 'Austin, TX', 'Nashville, TN', 'Portland, OR', 'Las Vegas, NV', 'Memphis, TN', 'Louisville, KY', 'Baltimore, MD', 'Milwaukee, WI', 'Albuquerque, NM', 'Tucson, AZ', 'Fresno, CA', 'Sacramento, CA', 'Mesa, AZ', 'Kansas City, MO', 'Atlanta, GA', 'Omaha, NE', 'Raleigh, NC', 'Miami, FL', 'Tampa, FL', 'New Orleans, LA', 'Cleveland, OH', 'Tulsa, OK', 'Orlando, FL', 'Minneapolis, MN', 'St. Louis, MO', 'Pittsburgh, PA', 'Cincinnati, OH', 'Boise, ID', 'Richmond, VA', 'Spokane, WA'];
const BUYER_NAMES = ['Margaret T.', 'Robert K.', 'Dorothy M.', 'James L.', 'Patricia H.', 'William B.', 'Barbara S.', 'Richard D.', 'Susan W.', 'Joseph C.', 'Nancy R.', 'Thomas G.', 'Karen A.', 'Charles F.', 'Betty P.', 'Donald E.', 'Helen N.', 'George M.', 'Sandra J.', 'Kenneth W.', 'Donna B.', 'Steven R.', 'Carol T.', 'Edward H.', 'Ruth L.', 'Brian K.', 'Sharon D.', 'Ronald S.', 'Laura G.', 'Anthony C.', 'Deborah F.', 'Kevin P.', 'Linda E.', 'Mark N.', 'Cynthia M.', 'Frank J.', 'Kathleen W.', 'Jerry B.', 'Angela R.', 'Dennis T.', 'Virginia H.', 'Walter L.', 'Marie D.', 'Peter S.', 'Janet G.', 'Harold C.', 'Frances F.', 'Douglas P.', 'Ann E.', 'Henry N.'];
const FALLBACK_PRODUCTS: ScannedProduct[] = [{
  title: 'The Senior Benefit Unlocker',
  editionLabel: 'Complete Bundle',
  priceNumber: 14,
  priceText: '$14.00',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/gpWjbD8KhtwUVrhi7qUaGc/The_Senior_Benefit_Unlocker_main_image.png'
}, {
  title: 'The Senior Benefit Unlocker',
  editionLabel: 'Guide + All Upgrades',
  priceNumber: 32,
  priceText: '$32.00',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/gpWjbD8KhtwUVrhi7qUaGc/The_Senior_Benefit_Unlocker_main_image.png'
}, {
  title: 'The Senior Benefit Unlocker',
  editionLabel: 'Guide + 1 Upgrade',
  priceNumber: 23,
  priceText: '$23.00',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/gpWjbD8KhtwUVrhi7qUaGc/The_Senior_Benefit_Unlocker_main_image.png'
}];
function scanPageForProducts(): ScannedProduct[] {
  const products: ScannedProduct[] = [];
  try {
    // Try JSON-LD first
    const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
    jsonLdScripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '');
        if (data['@type'] === 'Product' && data.offers) {
          const offer = Array.isArray(data.offers) ? data.offers[0] : data.offers;
          products.push({
            title: data.name || 'Product',
            editionLabel: data.description?.substring(0, 30) || '',
            priceNumber: parseFloat(offer.price) || 0,
            priceText: `$${parseFloat(offer.price).toFixed(2)}`,
            thumbnailUrl: (Array.isArray(data.image) ? data.image[0] : data.image) || ''
          });
        }
      } catch {
        /* skip invalid JSON-LD */
      }
    });
    if (products.length > 0) return products;
    // Scan for price patterns near CTAs
    const priceRegex = /\$\s?(\d+(?:\.\d{2})?)/g;
    const allText = document.body.innerText || '';
    const priceMatches = allText.match(priceRegex);
    if (priceMatches && priceMatches.length > 0) {
      // Find product images
      const productImages = document.querySelectorAll('img[alt*="Senior"], img[alt*="Benefit"], img[alt*="Guide"]');
      const thumbUrl = productImages.length > 0 ? (productImages[0] as HTMLImageElement).src : '';
      // Find the main product title from h1 or h2
      const h1 = document.querySelector('h1');
      const title = h1?.textContent?.trim().substring(0, 60) || 'The Senior Benefit Unlocker';
      // Extract unique prices
      const uniquePrices = [...new Set(priceMatches.map(p => parseFloat(p.replace(/\$\s?/, ''))))].filter(p => p > 0 && p < 100).sort((a, b) => a - b);
      uniquePrices.forEach(price => {
        products.push({
          title: 'The Senior Benefit Unlocker',
          editionLabel: price <= 14 ? 'Complete Bundle' : price <= 23 ? 'Guide + 1 Upgrade' : 'Guide + All Upgrades',
          priceNumber: price,
          priceText: `$${price.toFixed(2)}`,
          thumbnailUrl: thumbUrl
        });
      });
    }
  } catch {
    /* fallback below */
  }
  return products.length > 0 ? products : [];
}
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function pickWeightedProduct(products: ScannedProduct[]): ScannedProduct {
  if (products.length === 0) return FALLBACK_PRODUCTS[0];
  // Weight toward the base $14 product (70%) vs upgrades (30%)
  const rand = Math.random();
  const baseProducts = products.filter(p => p.priceNumber <= 14);
  const upgradeProducts = products.filter(p => p.priceNumber > 14);
  if (baseProducts.length > 0 && upgradeProducts.length > 0) {
    return rand < 0.7 ? pickRandom(baseProducts) : pickRandom(upgradeProducts);
  }
  return pickRandom(products);
}
type Notification = {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
};
export function RecentPurchaseToast() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [visible, setVisible] = useState(false);
  const productsRef = useRef<ScannedProduct[]>([]);
  const nameHistoryRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const prefersReducedMotion = useRef(false);
  useEffect(() => {
    prefersReducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Scan products after a short delay to let the page render
    const scanTimer = setTimeout(() => {
      const scanned = scanPageForProducts();
      productsRef.current = scanned.length > 0 ? scanned : FALLBACK_PRODUCTS;
    }, 1000);
    return () => clearTimeout(scanTimer);
  }, []);
  const pickName = useCallback(() => {
    const history = nameHistoryRef.current;
    const available = BUYER_NAMES.filter(n => !history.includes(n));
    const name = available.length > 0 ? pickRandom(available) : pickRandom(BUYER_NAMES);
    history.push(name);
    if (history.length > 10) history.shift();
    return name;
  }, []);
  const showNotification = useCallback(() => {
    const catalog = productsRef.current.length > 0 ? productsRef.current : FALLBACK_PRODUCTS;
    const product = pickWeightedProduct(catalog);
    const name = pickName();
    const city = pickRandom(US_CITIES);
    idRef.current += 1;
    setNotification({
      id: idRef.current,
      name,
      city,
      product
    });
    setVisible(true);
    // Hide after 4 seconds
    setTimeout(() => {
      setVisible(false);
    }, 4000);
  }, [pickName]);
  useEffect(() => {
    // First notification after 3 seconds
    const firstTimer = setTimeout(() => {
      showNotification();
    }, 3000);
    // Then repeat at random intervals
    let intervalTimer: ReturnType<typeof setTimeout>;
    const scheduleNext = () => {
      const delay = 8000 + Math.random() * 7000; // 8-15 seconds
      intervalTimer = setTimeout(() => {
        showNotification();
        scheduleNext();
      }, delay);
    };
    // Start the recurring cycle after the first one
    const startRecurring = setTimeout(() => {
      scheduleNext();
    }, 3000 + 5000); // first toast at 3s + 4s visible + 1s buffer
    return () => {
      clearTimeout(firstTimer);
      clearTimeout(startRecurring);
      clearTimeout(intervalTimer);
    };
  }, [showNotification]);
  const reduced = prefersReducedMotion.current;
  return <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none" data-id="element-615">
      <AnimatePresence data-id="element-616">
        {visible && notification && <motion.div key={notification.id} initial={reduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 40
      }} animate={reduced ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={reduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 40
      }} transition={{
        duration: reduced ? 0.15 : 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white rounded-xl shadow-2xl border border-gray-200 px-4 py-3 flex items-center gap-3 max-w-sm w-[90vw] sm:w-auto" data-id="element-617">
            {notification.product.thumbnailUrl && <img src={notification.product.thumbnailUrl} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" data-id="element-618" />}
            <div className="min-w-0" data-id="element-619">
              <p className="text-sm font-semibold text-gray-900 leading-tight" data-id="element-620">
                {notification.name}{' '}
                <span className="font-normal text-gray-500" data-id="element-621">in</span>{' '}
                {notification.city}
              </p>
              <p className="text-xs text-gray-600 mt-0.5 leading-snug" data-id="element-622">
                just purchased{' '}
                <span className="font-semibold text-brand-dark" data-id="element-623">
                  {notification.product.editionLabel}
                </span>{' '}
                —{' '}
                <span className="font-bold text-brand-gold" data-id="element-624">
                  {notification.product.priceText}
                </span>
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5" data-id="element-625">
                {Math.floor(Math.random() * 8) + 1} minutes ago
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}